import deepEqual from 'deep-eql';
import { useEffect, useState } from 'react';

import { usePrevious } from '../../hooks/use-previous';
import { useToasts } from '../../hooks/use-toasts';
import { FlatNode } from './types';
import { Data } from './use-mindmap-data';

export interface NodeMapChange {
  addedNodes: FlatNode[];
  deletedNodes: FlatNode[];
  updatedNodes: { prev: FlatNode; new: FlatNode }[];
}

export type HistoryActions = {
  undo: (undoToStep?: number) => void;
  redo: (redoToStep?: number) => void;
  undoSelectable: boolean;
  redoSelectable: boolean;
};

export interface History {
  nodeMapChangeHistory: NodeMapChange[];
  currentUndoRedoStep: number;
  actions: HistoryActions;
}

type UseHistory = { data: Data };

export const useMindmapHistory = ({
  data: { nodeMap, setNodeMap, historizeNextMapChange, sethistorizeNextMapChange },
}: UseHistory): History => {
  const { showToast } = useToasts();
  const [nodeMapChangeHistory, setNodeMapChangeHistory] = useState<NodeMapChange[]>([]);
  const prevNodeMap = usePrevious(nodeMap);
  const [currentUndoStep, setCurrentUndoStep] = useState<number>(0);

  // historize node map changes
  useEffect(() => {
    if (historizeNextMapChange) return sethistorizeNextMapChange(false);
    if (!nodeMap) return setNodeMapChangeHistory([]);

    if (prevNodeMap) {
      // detect node map change
      const nodeMapChange: NodeMapChange = { addedNodes: [], deletedNodes: [], updatedNodes: [] };
      for (let [key, value] of Object.entries(nodeMap)) {
        if (!prevNodeMap[key]) {
          // detected added node
          nodeMapChange.addedNodes.push(value);
        } else if (!deepEqual(prevNodeMap[key], value)) {
          // detected updated node
          nodeMapChange.updatedNodes.push({ prev: prevNodeMap[key], new: value });
        }
      }
      for (let [key, value] of Object.entries(prevNodeMap)) {
        if (!nodeMap[key]) {
          // detected deleted node
          nodeMapChange.deletedNodes.push(value);
        }
      }

      // the user can't redo anything after a change
      setNodeMapChangeHistory((nodeMapChangeHistory) => [
        nodeMapChange,
        ...nodeMapChangeHistory.filter((_v, i) => i >= currentUndoStep),
      ]);
      setCurrentUndoStep(0);
    }
  }, [nodeMap]);

  const nothingToUndo = currentUndoStep >= nodeMapChangeHistory.length;
  const undo = (undoToStep?: number) => {
    // check if there is anything to undo
    if (nothingToUndo) {
      return showToast({ severity: 'info', summary: 'There is nothing to undo.' });
    }

    // to which step should the history be un-done
    undoToStep = undoToStep ? currentUndoStep + undoToStep - 1 : currentUndoStep;

    const newNodeMap = { ...nodeMap };
    // undo to undoToStep one step at a time
    for (let i = currentUndoStep; i <= undoToStep; i++) {
      // undo to last nodemap from history
      const undoTo = nodeMapChangeHistory[i];

      const { addedNodes, deletedNodes, updatedNodes } = undoTo;
      addedNodes.forEach((addedNode) => {
        // delete added nodes
        delete newNodeMap[addedNode.id];
      });
      deletedNodes.forEach((deletedNode) => {
        // add deleted nodes
        newNodeMap[deletedNode.id] = deletedNode;
      });
      updatedNodes.forEach((updatedNode) => {
        // revert updates
        newNodeMap[updatedNode.new.id] = updatedNode.prev;
      });
    }

    setCurrentUndoStep(undoToStep + 1);
    sethistorizeNextMapChange(true);
    setNodeMap(newNodeMap);
  };

  const nothingToRedo = currentUndoStep == 0;
  const redo = (redoToStep?: number) => {
    // check if there is anything to redo
    if (nothingToRedo) {
      return showToast({ severity: 'info', summary: 'There is nothing to redo.' });
    }

    // to which step should the history be re-done
    redoToStep = redoToStep ? currentUndoStep - redoToStep : currentUndoStep - 1;

    const newNodeMap = { ...nodeMap };
    // redo to redoToStep one step at a time
    for (let i = currentUndoStep - 1; i >= redoToStep; i--) {
      const redoTo = nodeMapChangeHistory[i];
      const { addedNodes, deletedNodes, updatedNodes } = redoTo;

      addedNodes.forEach((addedNode) => {
        // add added nodes
        newNodeMap[addedNode.id] = addedNode;
      });
      deletedNodes.forEach((deletedNode) => {
        // delete deleted nodes
        delete newNodeMap[deletedNode.id];
      });
      updatedNodes.forEach((updatedNode) => {
        // apply updates
        newNodeMap[updatedNode.prev.id] = updatedNode.new;
      });
    }

    sethistorizeNextMapChange(true);
    setCurrentUndoStep(redoToStep);
    setNodeMap(newNodeMap);
  };

  const actions: HistoryActions = {
    undo,
    redo,
    undoSelectable: !nothingToUndo,
    redoSelectable: !nothingToRedo,
  };

  return { actions, nodeMapChangeHistory, currentUndoRedoStep: currentUndoStep };
};
